import React, { type ReactNode, type FunctionComponent, useState, useEffect } from 'react'
import { Select, fontSize } from '@retailer-platform/shared-components'
import { type Styles } from 'react-select'
import { type ControllerProps } from 'utils/react-hook-form/reactHookForm.types'
import { useDashMessage } from 'intl/intl.hooks'
import { type SelectOptionTypeIntl } from 'domains/merchandising/types/heroBanner.types'
import { type DashMessages } from 'intl/intl.types'
import { MerchandisingFormInputWrapper } from 'domains/merchandising/components/form-input/MerchandisingFormInputWrapper'

interface Props extends Omit<ControllerProps, 'name'> {
  options: SelectOptionTypeIntl[]
  identifier?: string
  labelId?: DashMessages
  labelPrefix?: ReactNode
  defaultValue?: string
  onChange?: (opt: SelectOptionTypeIntl) => void
}

const controlStyles: Styles = {
  control: () => ({
    height: 46,
    fontSize: fontSize.X15,
    '> :last-child': {
      margin: 'auto',
    },
  }),
}

const optionFromValue = (options: SelectOptionTypeIntl[] = [], value: string, isMulti: boolean) => {
  if (isMulti) return options.filter(opt => value?.includes(opt.value))

  return options.find(opt => opt.value === value)
}

export const MerchandisingFormSelect: FunctionComponent<Props> = ({
  labelId,
  labelPrefix = '',
  identifier,
  options,
  isMulti,
  form,
  required,
  onChange,
  defaultValue,
  'data-testid': dataTestId,
  ...restProps
}) => {
  const { setValue, getValues, register } = form || {}

  const [selectedOption, setSelectedOption] = useState(null)
  const label = labelPrefix + useDashMessage(labelId)

  useEffect(() => {
    // Set the initial value whenever the options change
    const value = defaultValue || getValues?.(identifier)
    setSelectedOption(optionFromValue(options, value, isMulti))
  }, [options, identifier, getValues, isMulti, defaultValue])

  const handleOnChange = event => {
    if (form) {
      // when isMulti, event is actually an array of selected options
      const value = isMulti ? event && event.map(opt => opt.value) : event.value
      setValue(identifier, value)
    }
    setSelectedOption(event)
    onChange?.(event)
  }

  return (
    <MerchandisingFormInputWrapper label={label} data-testid={dataTestId}>
      <Select
        {...restProps}
        value={selectedOption}
        onChange={handleOnChange}
        options={options}
        placeholder={label}
        styles={controlStyles}
        isMulti={isMulti}
      />
      {form && <input name={identifier} ref={register({ required })} type="hidden" />}
    </MerchandisingFormInputWrapper>
  )
}
